import React, { useState } from 'react';
import Image from 'components/AppImage';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

function StarRating({ rating, count }) {
  return (
    <div className="flex items-center gap-1">
      {[1, 2, 3, 4, 5]?.map((star) => (
        <Icon
          key={star}
          name="Star"
          size={12}
          color={star <= Math.round(rating) ? 'var(--color-accent)' : 'var(--color-border)'}
          strokeWidth={star <= Math.round(rating) ? 0 : 1.5}
          style={{ fill: star <= Math.round(rating) ? 'var(--color-accent)' : 'transparent' }}
        />
      ))}
      <span className="font-caption text-xs ml-1" style={{ color: 'var(--color-muted-foreground)' }}>
        ({count})
      </span>
    </div>
  );
}

export default function ProductCard({ product, onAddToCart, onViewDetail, isInCart, viewMode = 'grid' }) {
  const [saved, setSaved] = useState(false);
  const [addedAnim, setAddedAnim] = useState(false);

  const handleAddToCart = (e) => {
    e?.stopPropagation();
    onAddToCart(product);
    setAddedAnim(true);
    setTimeout(() => setAddedAnim(false), 1500);
  };

  if (viewMode === 'list') {
    return (
      <div
        className="flex gap-4 p-4 rounded-xl border cursor-pointer transition-all duration-200 hover:shadow-md w-full"
        style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)' }}
        onClick={() => onViewDetail(product)}
      >
        <div className="w-24 h-24 md:w-32 md:h-32 rounded-lg overflow-hidden flex-shrink-0">
          <Image src={product?.image} alt={product?.imageAlt} className="w-full h-full object-cover" />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-start justify-between gap-2">
              <div>
                {product?.winVerified && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-caption text-xs font-medium mb-1"
                    style={{ background: 'rgba(133,113,77,0.12)', color: 'var(--color-accent)' }}>
                    <Icon name="Crown" size={10} color="var(--color-accent)" />WIN Verified
                  </span>
                )}
                <h3 className="font-heading font-semibold text-base md:text-lg line-clamp-1" style={{ color: 'var(--color-foreground)' }}>
                  {product?.name}
                </h3>
                <p className="font-caption text-xs mt-0.5" style={{ color: 'var(--color-accent)' }}>{product?.seller}</p>
              </div>
              <button onClick={(e) => { e?.stopPropagation(); setSaved(!saved); }} className="p-1.5 flex-shrink-0">
                <Icon name="Bookmark" size={16} color={saved ? 'var(--color-accent)' : 'var(--color-muted-foreground)'}
                  style={{ fill: saved ? 'var(--color-accent)' : 'transparent' }} />
              </button>
            </div>
            <p className="font-caption text-sm mt-1 line-clamp-2" style={{ color: 'var(--color-muted-foreground)' }}>{product?.description}</p>
            <StarRating rating={product?.rating} count={product?.reviewCount} />
          </div>
          <div className="flex items-center justify-between mt-2">
            <span className="font-data font-semibold text-lg" style={{ color: 'var(--color-foreground)' }}>${product?.price?.toFixed(2)}</span>
            <Button variant="default" size="sm" onClick={handleAddToCart} iconName={addedAnim ? 'Check' : 'ShoppingCart'} iconPosition="left">
              {addedAnim ? 'Added!' : isInCart ? 'In Cart' : 'Add to Cart'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex flex-col rounded-xl border overflow-hidden cursor-pointer transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 w-full"
      style={{ background: 'var(--color-card)', borderColor: 'var(--color-border)', borderTop: '3px solid var(--color-accent)' }}
      onClick={() => onViewDetail(product)}
    >
      <div className="relative h-48 md:h-52 overflow-hidden">
        <Image src={product?.image} alt={product?.imageAlt} className="w-full h-full object-cover" />
        {product?.winVerified && (
          <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 rounded-full"
            style={{ background: 'var(--color-primary)', border: '1px solid var(--color-accent)' }}>
            <Icon name="Crown" size={11} color="var(--color-accent)" />
            <span className="font-caption text-xs font-medium" style={{ color: 'var(--color-accent)' }}>WIN Verified</span>
          </div>
        )}
        <button
          onClick={(e) => { e?.stopPropagation(); setSaved(!saved); }}
          className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center transition-all"
          style={{ background: 'rgba(255,255,255,0.9)' }}
          aria-label={saved ? 'Remove from saved' : 'Save product'}
        >
          <Icon name="Bookmark" size={15} color={saved ? 'var(--color-accent)' : 'var(--color-muted-foreground)'}
            style={{ fill: saved ? 'var(--color-accent)' : 'transparent' }} />
        </button>
        {product?.badge && (
          <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded font-caption text-xs font-semibold"
            style={{ background: 'var(--color-accent)', color: 'var(--color-accent-foreground)' }}>
            {product?.badge}
          </div>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        <div className="flex items-center gap-1.5 mb-1">
          <span className="font-caption text-xs px-2 py-0.5 rounded-full"
            style={{ background: 'var(--color-muted)', color: 'var(--color-muted-foreground)' }}>
            {product?.category}
          </span>
        </div>
        <h3 className="font-heading font-semibold text-base line-clamp-1 mb-0.5" style={{ color: 'var(--color-foreground)' }}>
          {product?.name}
        </h3>
        <p className="font-caption text-xs mb-1" style={{ color: 'var(--color-accent)' }}>{product?.seller}</p>
        <p className="font-caption text-sm line-clamp-2 mb-2 flex-1" style={{ color: 'var(--color-muted-foreground)' }}>
          {product?.description}
        </p>
        <StarRating rating={product?.rating} count={product?.reviewCount} />
        <div className="flex items-center justify-between mt-3 pt-3 border-t" style={{ borderColor: 'var(--color-border)' }}>
          <span className="font-data font-bold text-lg" style={{ color: 'var(--color-foreground)' }}>
            ${product?.price?.toFixed(2)}
          </span>
          <Button
            variant="default"
            size="sm"
            onClick={handleAddToCart}
            iconName={addedAnim ? 'Check' : 'ShoppingCart'}
            iconPosition="left"
          >
            {addedAnim ? 'Added!' : isInCart ? 'In Cart' : 'Add to Cart'}
          </Button>
        </div>
      </div>
    </div>
  );
}